import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { Plus, Trash2, Upload, Save, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import './QuizBuilder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function QuizBuilder() {
  const { t } = useTranslation();
  const { user } = React.useContext(AuthContext);
  const [quizData, setQuizData] = useState({
    title: '',
    difficulty: 'easy',
    subject: 'math',
    questions: [
      {
        question: '',
        image: null,
        options: [
          { text: '', image: null, isCorrect: true },
          { text: '', image: null, isCorrect: false },
          { text: '', image: null, isCorrect: false }
        ]
      }
    ]
  });
  
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiMode, setAiMode] = useState('title');
  const [aiInput, setAiInput] = useState('');
  const [aiNumQuestions, setAiNumQuestions] = useState(5);
  const [generating, setGenerating] = useState(false);
  
  const handleImageUpload = async (file, questionIndex, optionIndex = null) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      const newQuestions = [...quizData.questions];
      if (optionIndex !== null) {
        newQuestions[questionIndex].options[optionIndex].image = response.data.url;
      } else {
        newQuestions[questionIndex].image = response.data.url;
      }
      setQuizData({ ...quizData, questions: newQuestions });
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    }
  };
  
  const addQuestion = () => {
    setQuizData({
      ...quizData,
      questions: [
        ...quizData.questions,
        {
          question: '',
          image: null,
          options: [
            { text: '', image: null, isCorrect: true },
            { text: '', image: null, isCorrect: false },
            { text: '', image: null, isCorrect: false }
          ]
        }
      ]
    });
  };
  
  const deleteQuestion = (index) => {
    const newQuestions = quizData.questions.filter((_, i) => i !== index);
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const addOption = (questionIndex) => {
    const newQuestions = [...quizData.questions];
    newQuestions[questionIndex].options.push({ text: '', image: null, isCorrect: false });
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const deleteOption = (questionIndex, optionIndex) => {
    const newQuestions = [...quizData.questions];
    newQuestions[questionIndex].options = newQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const updateQuestion = (index, field, value) => {
    const newQuestions = [...quizData.questions];
    newQuestions[index][field] = value;
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const updateOption = (qIndex, oIndex, field, value) => {
    const newQuestions = [...quizData.questions];
    if (field === 'isCorrect') {
      newQuestions[qIndex].options.forEach((opt, i) => {
        opt.isCorrect = i === oIndex;
      });
    } else {
      newQuestions[qIndex].options[oIndex][field] = value;
    }
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const saveQuiz = async () => {
    if (!quizData.title) {
      toast.error('Please enter a quiz title');
      return;
    }
    
    const formattedQuestions = quizData.questions.map(q => ({
      question: q.question,
      image: q.image,
      options: q.options.map(o => o.text),
      correct_answer: q.options.find(o => o.isCorrect)?.text || q.options[0].text,
      type: 'multiple_choice',
      option_images: q.options.map(o => o.image || null)
    }));
    
    try {
      await axios.post(`${API}/quizzes`, {
        ...quizData,
        questions: formattedQuestions
      }, { withCredentials: true });
      
      toast.success('Quiz saved successfully!');
      setQuizData({
        title: '',
        difficulty: 'easy',
        subject: 'math',
        questions: [{
          question: '',
          image: null,
          options: [
            { text: '', image: null, isCorrect: true },
            { text: '', image: null, isCorrect: false },
            { text: '', image: null, isCorrect: false }
          ]
        }]
      });
    } catch (error) {
      console.error('Failed to save quiz:', error);
      toast.error('Failed to save quiz');
    }
  };
  
  const generateAIQuiz = async () => {
    if (!aiInput) {
      toast.error('Please enter some text');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-quiz`, {
        mode: aiMode,
        input_text: aiInput,
        num_questions: aiNumQuestions
      }, { withCredentials: true });
      
      toast.success('AI Quiz generated successfully!');
      setAiDialogOpen(false);
      setAiInput('');
      
      const quiz = response.data;
      setQuizData({
        title: quiz.title,
        difficulty: quiz.difficulty,
        subject: quiz.subject,
        questions: quiz.questions.map(q => ({
          question: q.question,
          image: null,
          options: q.options.map((opt, i) => ({
            text: opt,
            image: null,
            isCorrect: opt === q.correct_answer
          }))
        }))
      });
      
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate quiz');
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="quiz-builder-page" data-testid="quiz-builder">
      <div className="builder-container">
        <div className="builder-header">
          <h1>Quiz Builder</h1>
          <div className="header-actions">
            <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
              <DialogTrigger asChild>
                <Button className="ai-btn" data-testid="ai-generator-button">
                  <Sparkles size={20} />
                  AI Quiz Generator
                </Button>
              </DialogTrigger>
              <DialogContent className="ai-dialog">
                <DialogHeader>
                  <DialogTitle>AI Quiz Generator</DialogTitle>
                </DialogHeader>
                
                <div className="ai-form">
                  <div className="ai-mode-selector">
                    <button
                      className={`mode-btn ${aiMode === 'title' ? 'active' : ''}`}
                      onClick={() => setAiMode('title')}
                      data-testid="mode-title"
                    >
                      Generate from Title
                    </button>
                    <button
                      className={`mode-btn ${aiMode === 'text' ? 'active' : ''}`}
                      onClick={() => setAiMode('text')}
                      data-testid="mode-text"
                    >
                      Generate from Text
                    </button>
                  </div>
                  
                  <div className="form-group">
                    <Label>
                      {aiMode === 'title' ? 'Quiz Title (any language)' : 'Text or Story (any language)'}
                    </Label>
                    {aiMode === 'title' ? (
                      <Input
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        placeholder="e.g., Math for kids age 5"
                        data-testid="ai-input"
                      />
                    ) : (
                      <Textarea
                        value={aiInput}
                        onChange={(e) => setAiInput(e.target.value)}
                        placeholder="Paste a story or text here..."
                        rows={6}
                        data-testid="ai-input"
                      />
                    )}
                  </div>
                  
                  <div className="form-group">
                    <Label>Number of Questions</Label>
                    <Input
                      type="number"
                      min="3"
                      max="10"
                      value={aiNumQuestions}
                      onChange={(e) => setAiNumQuestions(parseInt(e.target.value))}
                      data-testid="ai-num-questions"
                    />
                  </div>
                  
                  <Button
                    onClick={generateAIQuiz}
                    disabled={generating}
                    className="generate-btn"
                    data-testid="generate-button"
                  >
                    {generating ? 'Generating...' : 'Generate Quiz'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button onClick={saveQuiz} className="save-btn" data-testid="save-quiz-button">
              <Save size={20} />
              Save Quiz
            </Button>
          </div>
        </div>
        
        <div className="quiz-settings">
          <Input
            value={quizData.title}
            onChange={(e) => setQuizData({ ...quizData, title: e.target.value })}
            placeholder="Quiz Title"
            className="title-input"
            data-testid="quiz-title-input"
          />
          
          <div className="settings-row">
            <Select
              value={quizData.difficulty}
              onValueChange={(value) => setQuizData({ ...quizData, difficulty: value })}
            >
              <SelectTrigger data-testid="difficulty-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={quizData.subject}
              onValueChange={(value) => setQuizData({ ...quizData, subject: value })}
            >
              <SelectTrigger data-testid="subject-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="math">Math</SelectItem>
                <SelectItem value="science">Science</SelectItem>
                <SelectItem value="reading">Reading</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="questions-list">
          {quizData.questions.map((question, qIndex) => (
            <div key={qIndex} className="question-card">
              <div className="question-header">
                <span className="question-number">Question {qIndex + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteQuestion(qIndex)}
                  className="delete-btn"
                  data-testid={`delete-question-${qIndex}`}
                >
                  <Trash2 size={18} />
                </Button>
              </div>
              
              <div className="question-content">
                <Textarea
                  value={question.question}
                  onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                  placeholder="Enter your question"
                  rows={2}
                  data-testid={`question-text-${qIndex}`}
                />
                
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    id={`q-img-${qIndex}`}
                    onChange={(e) => handleImageUpload(e.target.files[0], qIndex)}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor={`q-img-${qIndex}`} className="upload-btn">
                    <Upload size={16} />
                    {question.image ? 'Change Image' : 'Add Image'}
                  </label>
                  {question.image && (
                    <img src={question.image} alt="Question" className="preview-img" />
                  )}
                </div>
              </div>
              
              <div className="options-section">
                <Label>Options:</Label>
                {question.options.map((option, oIndex) => (
                  <div key={oIndex} className="option-row" data-testid={`option-row-${qIndex}-${oIndex}`}>
                    <input
                      type="radio"
                      name={`correct-${qIndex}`}
                      checked={option.isCorrect}
                      onChange={() => updateOption(qIndex, oIndex, 'isCorrect', true)}
                      data-testid={`option-radio-${qIndex}-${oIndex}`}
                    />
                    <Input
                      value={option.text}
                      onChange={(e) => updateOption(qIndex, oIndex, 'text', e.target.value)}
                      placeholder={`Option ${oIndex + 1}`}
                      data-testid={`option-text-${qIndex}-${oIndex}`}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      id={`opt-img-${qIndex}-${oIndex}`}
                      onChange={(e) => handleImageUpload(e.target.files[0], qIndex, oIndex)}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor={`opt-img-${qIndex}-${oIndex}`} className="upload-btn-sm">
                      <Upload size={14} />
                    </label>
                    {option.image && (
                      <img src={option.image} alt={`Option ${oIndex + 1}`} className="option-preview" />
                    )}
                    {question.options.length > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteOption(qIndex, oIndex)}
                        data-testid={`delete-option-${qIndex}-${oIndex}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addOption(qIndex)}
                  className="add-option-btn"
                  data-testid={`add-option-${qIndex}`}
                >
                  <Plus size={16} />
                  Add Option
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        <Button
          onClick={addQuestion}
          className="add-question-btn"
          data-testid="add-question-button"
        >
          <Plus size={20} />
          Add Question
        </Button>
      </div>
    </div>
  );
}

export default QuizBuilder;